/**
 * V2 Stepper Style Configuration
 *
 * Factory for style section configs adapted to V2 stepper schema paths.
 * V2 uses .appearance instead of .styles, .layout.insets instead of .box.styles.
 *
 * Adapted from V1 progressBarSectionConfig.ts:
 * - V1: progressBar.items.0.content.title.styles.* -> V2: content.stepper.items.0.content.title.appearance.*
 * - V1: progressBar.box.styles.* -> V2: content.stepper.layout.insets.*
 *
 * Sprint: schema-v2-migration, Sprint 4 fix
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import type { ListStyleSectionConfig } from '../types/listSection.types';
import {
  createV2TextStyleFields,
  createV2ImageStyleFields,
} from './v2OptionsListStyleConfig';
import {
  NUMBER_0_50_OPTIONS,
  NUMBER_0_100_OPTIONS,
} from './atomicFields';

// ============================================
// V2 Stepper Bar Style Fields
// ============================================

/**
 * Create V2 stepper bar style fields
 */
function createV2StepperBarFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.appearance.trackColor`,
      label: 'track color',
      type: 'color',
      defaultValue: '#E0E0E0',
      isNullable: true,
      guideKey: 'screenedit-stepper-track-color',
    },
    {
      path: `${basePath}.appearance.fillColor`,
      label: 'fill color',
      type: 'color',
      defaultValue: '#4CAF50',
      isNullable: true,
      guideKey: 'screenedit-stepper-fill-color',
    },
    {
      path: `${basePath}.appearance.thickness`,
      label: 'thickness',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 4,
      defaultScrollValue: 4,
      isNullable: true,
      guideKey: 'screenedit-stepper-thickness',
    },
    {
      path: `${basePath}.appearance.trackThickness`,
      label: 'track thickness',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 4,
      defaultScrollValue: 4,
      isNullable: true,
      guideKey: 'screenedit-stepper-track-thickness',
    },
  ];
}

/**
 * Create V2 image container height field
 */
function createV2ImageContainerFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.appearance.dimensions.heightPercentage`,
      label: 'container height %',
      type: 'numberCombo',
      options: NUMBER_0_100_OPTIONS,
      defaultValue: 50,
      defaultScrollValue: 50,
      isNullable: true,
      guideKey: 'screenedit-stepper-container-height',
    },
  ];
}

// ============================================
// Main Factory
// ============================================

/**
 * Create V2 stepper style sections
 *
 * Returns 7 collapsible sections matching V1 ProgressBarStyleSections:
 * 1. Progress Bar styles (colors, thickness + padding)
 * 2. Media/Text Container styles (dimensions.heightPercentage) - conditional on kind != 'orbital'
 * 3. Title styles
 * 4. Subtitle styles
 * 5. Description styles
 * 6. Image styles - conditional on kind != 'orbital'
 */
export function createV2StepperStyleSections(): ListStyleSectionConfig[] {
  const stepperBase = 'content.stepper';

  return [
    // Stepper bar styles
    {
      id: 'stepperBarStyles',
      title: 'Progress Bar styles',
      defaultExpanded: true,
      fields: createV2StepperBarFields(stepperBase),
      paddingConfig: {
        basePath: `${stepperBase}.layout.insets`,
        label: 'Progress Bar padding',
      },
    },
    // Media/text container styles — hidden when kind = 'orbital'
    {
      id: 'imageContainerStyles',
      title: 'Media/Text Container styles',
      defaultExpanded: false,
      fields: createV2ImageContainerFields(stepperBase),
      visibilityCondition: (kind: string) => kind !== 'orbital',
    },
    // Title styles
    {
      id: 'titleStyles',
      title: 'Title styles',
      defaultExpanded: false,
      fields: createV2TextStyleFields(`${stepperBase}.items.0.content.title`),
      paddingConfig: {
        basePath: `${stepperBase}.items.0.content.title.layout.insets`,
        label: 'Title padding',
      },
    },
    // Subtitle styles
    {
      id: 'subtitleStyles',
      title: 'Subtitle styles',
      defaultExpanded: false,
      fields: createV2TextStyleFields(`${stepperBase}.items.0.content.subtitle`),
      paddingConfig: {
        basePath: `${stepperBase}.items.0.content.subtitle.layout.insets`,
        label: 'Subtitle padding',
      },
    },
    // Description styles
    {
      id: 'descriptionStyles',
      title: 'Description styles',
      defaultExpanded: false,
      fields: createV2TextStyleFields(`${stepperBase}.items.0.content.description`),
      paddingConfig: {
        basePath: `${stepperBase}.items.0.content.description.layout.insets`,
        label: 'Description padding',
      },
    },
    // Image styles — hidden when kind = 'orbital'
    {
      id: 'imageStyles',
      title: 'Image styles',
      defaultExpanded: false,
      fields: createV2ImageStyleFields(`${stepperBase}.items.0.content.image`),
      paddingConfig: {
        basePath: `${stepperBase}.items.0.content.image.layout.insets`,
        label: 'Image padding',
      },
      visibilityCondition: (kind: string) => kind !== 'orbital',
    },
  ];
}
